// ct-auth, NOT "@auth0/auth0-react". This file was missed in the IdP migration.
// With AUTH_PROVIDER=keycloak the app mounts react-oidc-context and there is no
// <Auth0Provider>, so the raw hook returns the SDK's initialContext: `user` is
// undefined and `getAccessTokenSilently` is a stub that throws. Every query below is
// gated on `user?.sub`, so all ~25 of them silently evaluated `enabled: false` and
// never ran — the assume-role org picker stayed permanently empty, and the builder
// mutations threw "You forgot to wrap your component in <Auth0Provider>" on use.
import { useAuth0 } from "ct-auth"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "react-toastify"
import { userOrg } from "./authUtils"
import {
  aiEditCtfInject,
  aiEditCtfRunOfShowPart,
  analyzeSimulationAdaptive,
  fetchOrgCoreSystemsProfiles,
  fetchOrgIncidentResponsePlan,
  fetchOrgIRPolicys,
  fetchScenarioSuggestions,
  fetchSimulationDrafts,
  fetchSimulationProfilesList,
  getOrgs,
  getSimulationById,
  initSimulation,
  promptChaining,
  promptChainingStream,
  refreshScenarioSuggestions,
  simulationPrompt,
  deriveTimelineTableStream,
  streamSimulationPrompt,
  syncSimulationToArangoDB,
  updateOrgVariables,
  updateSimulation,
  uploadingExcel,
} from "./data"

export const QUERY_KEYS = {
  ORG_CORE_SYSTEMS_PROFILES: (userSub, orgId) => [`GET_ORG_CORE_SYSTEMS_PROFILES-${userSub}`, orgId],
  ORG_INCIDENT_RESPONSE_PLAN: (userSub, orgId) => [`GET_ORG_INCIDENT_RESPONSE_PLAN-${userSub}`, orgId],
  ORG_IR_POLICYS: (userSub, orgId) => [`GET_ORG_IR_POLICYS-${userSub}`, orgId],
  SCENARIO_SUGGESTIONS: (userSub, orgId, profileId, themeKey, category) => [
    "SCENARIO_SUGGESTIONS",
    userSub,
    orgId,
    profileId ?? "primary",
    themeKey || "default",
    category || "cyber",
  ],
  SIMULATION_PROFILE_LIST: (userSub) => [`GET_SIMULATION_PROFILE_LIST-${userSub}`],
  SIMULATION_BY_ID: (userSub, simulationId) => [`GET_SIMULATION_BY_ID-${userSub}`, simulationId],
  SIMULATION_DRAFTS: (userSub) => [`SIMULATION_DRAFTS-${userSub}`],
  GET_ORGS: (userSub) => [`GET_ORGS-${userSub}`],
}

// ── "Suggested for you" theme entries ─────────────────────────────────────────────────────
// Each theme/topic the user has ever asked for keeps its OWN cached suggestion list, keyed by
// the normalised theme. This MUST match the server's themeKeyFor (server/utils/
// scenario-suggestions-utils.js -> sanitizeSuggestionTheme + lowercase) or a selected entry is
// fetched under a key that was never stored: strip control chars, collapse whitespace, trim,
// cap, lowercase.
export const SUGGESTION_THEME_MAX_LENGTH = 200
export const DEFAULT_SUGGEST_ENTRY_LABEL = "Cyber (default)"

export const normalizeSuggestTheme = (theme) => {
  if (typeof theme !== "string") return ""
  return (
    theme
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1f\x7f]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, SUGGESTION_THEME_MAX_LENGTH)
  )
}

export const themeKeyFor = (theme) => normalizeSuggestTheme(theme).toLowerCase()

// ── "Suggested for you" DOMAIN categories ─────────────────────────────────────────────────
// The dropdown is DYNAMIC per profile: the server returns bundle.categories (Cyber always
// first + LLM-derived company-specific domains, or the static 4 below for profiles that were
// never re-derived). The static list survives ONLY as the old-server fallback in
// suggestionCategoriesFrom. Keys are slugs; keep the shape rule + suggestionEntryKeyFor in
// lock-step with the server (scenario-suggestions-utils.js sanitizeCategoryKey /
// suggestionEntryKey): membership is enforced server-side where the doc is available.
export const DEFAULT_SUGGEST_CATEGORY = "cyber"
export const SUGGESTION_CATEGORIES = [
  { key: "cyber", label: "Cyber" },
  { key: "bcdr_it", label: "BC/DR – IT" },
  { key: "bcdr_business", label: "BC/DR – Business Operations" },
  { key: "crisis", label: "Other Crisis" },
]
const CATEGORY_KEY_RE = /^[a-z0-9][a-z0-9_]{0,39}$/
export const normalizeSuggestCategory = (raw) => {
  const key = typeof raw === "string" ? raw.trim().toLowerCase() : ""
  return CATEGORY_KEY_RE.test(key) ? key : DEFAULT_SUGGEST_CATEGORY
}

// The cache identity a list is stored/fetched under (must mirror the server, back-compat scheme):
//   cyber + no focus -> ""; cyber + focus -> "<themeKey>";
//   other + no focus -> "cat:<category>"; other + focus -> "cat:<category>|theme:<themeKey>".
export const suggestionEntryKeyFor = ({ category, theme } = {}) => {
  const cat = normalizeSuggestCategory(category)
  const tk = themeKeyFor(theme)
  if (cat === DEFAULT_SUGGEST_CATEGORY) return tk
  return tk ? `cat:${cat}|theme:${tk}` : `cat:${cat}`
}

// The profile's resolved categories (dynamic) with each one's cached status; static fallback for old servers.
export const suggestionCategoriesFrom = (data) =>
  Array.isArray(data?.categories) && data.categories.length
    ? data.categories
    : SUGGESTION_CATEGORIES.map((c) => ({ category: c.key, label: c.label, status: "none", generatedAt: null }))

// Old server builds return no `entries`, so always render at least the default entry.
export const suggestionEntriesFrom = (data) =>
  Array.isArray(data?.entries) && data.entries.length
    ? data.entries
    : [{ themeKey: "", label: DEFAULT_SUGGEST_ENTRY_LABEL, theme: "", generatedAt: data?.generatedAt ?? null }]

// A 'generating' marker is written before the LLM call, so a server restart or timeout can
// leave it set forever. Past this window treat it as abandoned: re-enable Refresh and stop
// polling, instead of locking the panel until the weekly cron.
export const GENERATING_STALE_MS = 10 * 60 * 1000

export const isSuggestGenerationStuck = ({ status, generatingSince, now = Date.now() }) => {
  if (status !== "generating") return false
  return typeof generatingSince !== "number" || now - generatingSince > GENERATING_STALE_MS
}

// The builder flags people named in a generated artifact who appear nowhere in the author's
// inputs (company profile, roles, brief) — usually invented. One sticky warning per artifact so
// the author fixes them before the scenario is published.
export const warnUnlistedNames = (metadata) => {
  const names = Array.isArray(metadata?.unlistedNames) ? metadata.unlistedNames.filter(Boolean) : []
  const toastId = `unlisted-names-${metadata?.variable || "generation"}`
  // One warning per artifact, always for its LATEST generation: a sticky toast with a fixed id would
  // otherwise keep the previous run's names (or linger after a clean regeneration).
  toast.dismiss(toastId)
  if (!names.length) return
  toast.warning(
    `Check these names: ${names.join(", ")}. They aren't in your company profile or brief, so they may be invented. Replace them with the real person's name or the role title.`,
    { toastId, autoClose: false }
  )
}

export const useGenerationApis = Object.freeze({
  /* QUERY */
  GET_ORG_CORE_SYSTEMS_PROFILES: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    const thisOrg = userOrg(user)
    return useQuery({
      queryKey: QUERY_KEYS.ORG_CORE_SYSTEMS_PROFILES(user?.sub, thisOrg),
      queryFn: async () => {
        if (user?.sub && thisOrg) {
          const accessToken = await getAccessTokenSilently()
          return await fetchOrgCoreSystemsProfiles(accessToken, user.sub, { orgId: thisOrg })
        }
        return []
      },
      enabled: !!(user?.sub && thisOrg),
      staleTime: 60 * 1000,
    })
  },

  GET_ORG_INCIDENT_RESPONSE_PLAN: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    const thisOrg = userOrg(user)
    return useQuery({
      queryKey: QUERY_KEYS.ORG_INCIDENT_RESPONSE_PLAN(user?.sub, thisOrg),
      queryFn: async () => {
        if (user?.sub && thisOrg) {
          const accessToken = await getAccessTokenSilently()
          return await fetchOrgIncidentResponsePlan(accessToken, user.sub, { orgId: thisOrg })
        }
        return ""
      },
      enabled: !!(user?.sub && thisOrg),
      staleTime: 60 * 1000, // 1 minute
    })
  },

  GET_ORG_IR_POLICYS: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    const thisOrg = userOrg(user)
    return useQuery({
      queryKey: QUERY_KEYS.ORG_IR_POLICYS(user?.sub, thisOrg),
      queryFn: async () => {
        if (user?.sub && thisOrg) {
          const accessToken = await getAccessTokenSilently()
          return await fetchOrgIRPolicys(accessToken, user.sub, { orgId: thisOrg })
        }
        return []
      },
      staleTime: 60 * 1000, // 1 minute
    })
  },

  GET_SIMULATION_PROFILE_LIST: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: QUERY_KEYS.SIMULATION_PROFILE_LIST(user?.sub),
      queryFn: async () => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          return await fetchSimulationProfilesList(accessToken, user.sub)
        }
      },
      enabled: !!user?.sub,
      staleTime: 60 * 1000, // 1 minute
    })
  },

  GET_SIMULATION_BY_ID: (simulationId) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: QUERY_KEYS.SIMULATION_BY_ID(user?.sub, simulationId),
      queryFn: async () => {
        if (user?.sub && simulationId) {
          const accessToken = await getAccessTokenSilently()
          return await getSimulationById(accessToken, user.sub, { simulationId })
        }
        return null
      },
      enabled: !!simulationId,
      staleTime: 5 * 1000, // 5 seconds
    })
  },

  GET_SIMULATION_DRAFTS: ({ enabled = true } = {}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: QUERY_KEYS.SIMULATION_DRAFTS(user?.sub),
      queryFn: async () => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          return await fetchSimulationDrafts(accessToken, user.sub)
        }
        return []
      },
      enabled: !!(user?.sub && enabled),
      staleTime: 30 * 1000,
    })
  },

  GET_ORGS: ({ enabled = true } = {}) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: QUERY_KEYS.GET_ORGS(user?.sub),
      queryFn: async () => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          return await getOrgs(accessToken, user.sub)
        }
        return []
      },
      enabled: !!(user?.sub && enabled),
      staleTime: 60 * 1000, // 1 minute
    })
  },

  GET_SCENARIO_SUGGESTIONS: ({ orgId, profileId = null, themeKey = "", category = DEFAULT_SUGGEST_CATEGORY }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: QUERY_KEYS.SCENARIO_SUGGESTIONS(user?.sub, orgId, profileId, themeKey, category),
      queryFn: async () => {
        if (user?.sub && orgId) {
          const accessToken = await getAccessTokenSilently()
          return await fetchScenarioSuggestions(accessToken, user.sub, { orgId, profileId, themeKey, category })
        }
      },
      enabled: !!user?.sub && !!orgId,
      staleTime: 30 * 1000,
      // Poll while a generation is in flight, but give up on an abandoned one instead of
      // polling a stuck 'generating' doc every 4s forever.
      refetchInterval: (query) => {
        const data = query.state.data
        if (data?.status !== "generating") return false
        return isSuggestGenerationStuck({ status: data.status, generatingSince: data.generatingSince }) ? false : 4000
      },
    })
  },

  ///////////////////////////////////////////////////////////////////////////////////////////////////////
  /* MUTATIONS */
  ///////////////////////////////////////////////////////////////////////////////////////////////////////

  REFRESH_SCENARIO_SUGGESTIONS: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ orgId, profileId, theme = null, category = null }) => {
        const accessToken = await getAccessTokenSilently()
        return await refreshScenarioSuggestions(accessToken, user.sub, { orgId, profileId, theme, category })
      },
      onSuccess: (_data, variables) => {
        // Prefix-invalidate ALL suggestion queries for this org: the mounted GET usually
        // runs with profileId=null (key "…primary") while the refresh knows the explicit
        // selectedProfileId — an exact-key invalidate misses the active query and the UI
        // keeps showing the stale list.
        queryClient.invalidateQueries({ queryKey: ["SCENARIO_SUGGESTIONS", user?.sub, variables.orgId] })
      },
    })
  },

  INIT_SIMULATION: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async () => {
        if (!user?.sub) throw new Error("User not available")
        const accessToken = await getAccessTokenSilently()
        return await initSimulation(accessToken, user.sub)
      },
      // cache invalidation is not needed here; caller will set scenarioData
      onError: (error) => {
        return error.message || "Failed to initialize simulation."
      },
    })
  },

  /* MUTATIONS */
  UPDATE_ORG_VARIABLES: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    const thisOrg = userOrg(user)

    return useMutation({
      mutationFn: async ({ coreSystems, irPolicy }) => {
        if (thisOrg) {
          const accessToken = await getAccessTokenSilently()
          return await updateOrgVariables(accessToken, user.sub, {
            coreSystems,
            irPolicy,
            orgId: thisOrg,
          })
        }
        return null
      },
      // onSuccess: () => {},
      onError: (error) => {
        return error.message || "Failed to update organization variables."
      },
    })
  },

  UPDATE_SIMULATION: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ simulationId, data }) => {
        const accessToken = await getAccessTokenSilently()
        return await updateSimulation(accessToken, user.sub, { simulationId, data })
      },
      onSuccess: (_data, variables) => {
        // Invalidate the specific simulation query so it refetches the updated simulation
        if (variables && variables.simulationId) {
          queryClient.invalidateQueries(QUERY_KEYS.SIMULATION_BY_ID(user?.sub, variables.simulationId))
        }
      },
      onError: (error) => {
        return error.message || "Failed to update simulation."
      },
    })
  },

  UPDATE_SIMULATION_PROMPT: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ simulationId, variable, dataDetail }) => {
        const accessToken = await getAccessTokenSilently()
        return await simulationPrompt(accessToken, user.sub, { simulationId, variable, dataDetail })
      },
      onSuccess: (_data, variables) => {
        // Invalidate the specific simulation query so it refetches the updated simulation prompt
        if (variables && variables.simulationId) {
          queryClient.invalidateQueries(QUERY_KEYS.SIMULATION_BY_ID(user?.sub, variables.simulationId))
        }
      },
      onError: (error) => {
        return error.message || "Failed to update simulation prompt."
      },
    })
  },

  DERIVE_TIMELINE_TABLE: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ simulationId, timeline, onRow }) => {
        const accessToken = await getAccessTokenSilently()
        return await deriveTimelineTableStream(accessToken, user.sub, { simulationId, timeline, onRow })
      },
    })
  },

  // Obtain the adaptive_spec for the screen-3 review card (get-or-compute; best-effort).
  ANALYZE_ADAPTIVE: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ simulationId, instruction, regenerate, reconcileText }) => {
        const accessToken = await getAccessTokenSilently()
        return await analyzeSimulationAdaptive(accessToken, user.sub, { simulationId, instruction, regenerate, reconcileText })
      },
    })
  },

  // CTF Questions stage: propose an AI rewrite of one question (not stored).
  AI_EDIT_CTF_INJECT: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ simulationId, injectId, instruction }) => {
        const accessToken = await getAccessTokenSilently()
        return await aiEditCtfInject(accessToken, user.sub, { simulationId, injectId, instruction })
      },
    })
  },

  AI_EDIT_CTF_RUN_OF_SHOW: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ simulationId, part, id, instruction }) => {
        const accessToken = await getAccessTokenSilently()
        return await aiEditCtfRunOfShowPart(accessToken, user.sub, { simulationId, part, id, instruction })
      },
    })
  },

  UPDATE_SIMULATION_PROMPT_STREAM: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ simulationId, variable, dataDetail, onChunk, onComplete, onError, onStage, signal }) => {
        const accessToken = await getAccessTokenSilently()
        return await streamSimulationPrompt(accessToken, user.sub, {
          simulationId,
          variable,
          dataDetail,
          onChunk,
          onComplete: (fullText, metadata) => {
            warnUnlistedNames(metadata)
            if (onComplete) onComplete(fullText, metadata)
          },
          onError,
          onStage,
          signal,
        })
      },
      onSuccess: (_data, variables) => {
        if (variables && variables.simulationId) {
          queryClient.invalidateQueries(QUERY_KEYS.SIMULATION_BY_ID(user?.sub, variables.simulationId))
        }
      },
      onError: (error) => {
        return error.message || "Failed to stream simulation prompt."
      },
    })
  },

  // "Describe a change" refine. Routed through the STREAMING builder endpoint (direct
  // via nginx, 600s) rather than the Hasura action (hard 300s ceiling): a full-artifact
  // rewrite can exceed 300s, and the action timeout is not raisable from the app.
  // The resolved shape is unchanged ({ llm_response }) so every existing caller — the
  // fullscreen review steps, the customizer, EditWorkstreamStatus — is unaffected.
  // `onChunk` is optional: callers that want live text can pass one.
  FETCH_PROMPT_CHAINING: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ simulationId, sessionId, promptData, onChunk }) => {
        const accessToken = await getAccessTokenSilently()
        const llmResponse = await promptChainingStream(accessToken, user.sub, { simulationId, sessionId, promptData }, onChunk)
        return { message: "Prompt submitted successfully", llm_response: llmResponse }
      },
      onError: (error) => {
        return error.message || "Failed to update simulation prompt."
      },
    })
  },

  UPLOAD_EXCEL: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ simulationId, orgAccess }) => {
        const accessToken = await getAccessTokenSilently()
        const { result, message } = await uploadingExcel(accessToken, user.sub, { simulationId, orgAccess })
        if (message) {
          throw new Error(message)
        }
        return result
      },
      onSuccess: (_data, _variables) => {
        queryClient.invalidateQueries([`SCENARIO_LIST-${user?.sub}`])
        // A published simulation is no longer a draft — refresh the Drafts tab.
        queryClient.invalidateQueries([`SIMULATION_DRAFTS-${user?.sub}`])
      },
      onError: (error) => {
        return error.message || "Failed to upload Excel file."
      },
    })
  },

  SYNC_SIMULATION_TO_ARANGODB: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ simulationId, orgAccess, testKey }) => {
        const accessToken = await getAccessTokenSilently()
        const { ok, message } = await syncSimulationToArangoDB(accessToken, user.sub, { simulationId, orgAccess, testKey })
        if (!ok && message) {
          throw new Error(message)
        }
        return { ok }
      },
    })
  },
})
